import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ProducerFormData, EMPTY_FORM_DATA } from '../types';

interface UseProducerEditorStateOptions {
  initialData?: Partial<ProducerFormData>;
  onSave?: (data: ProducerFormData) => Promise<void>;
}

export function useProducerEditorState({ initialData, onSave }: UseProducerEditorStateOptions = {}) {
  const [formData, setFormData] = useState<ProducerFormData>(() => ({
    ...EMPTY_FORM_DATA,
    ...initialData,
    galleryUrls: initialData?.galleryUrls?.length ? initialData.galleryUrls : [''],
  }));

  const [originalData, setOriginalData] = useState<ProducerFormData>(formData);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ProducerFormData, string>>>({});

  // Use a ref for hasLocalEdits so changes don't re-trigger the initialData effect
  const hasLocalEditsRef = useRef(false);

  useEffect(() => {
    if (initialData) {
      const newData = {
        ...EMPTY_FORM_DATA,
        ...initialData,
        galleryUrls: initialData?.galleryUrls?.length ? initialData.galleryUrls : [''],
      };
      // Always update originalData to the latest server state
      setOriginalData(newData);
      // Only reset formData if user hasn't made local edits
      if (!hasLocalEditsRef.current) {
        setFormData(newData);
      }
    }
  }, [initialData]);

  // Check if form has unsaved changes
  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Update a single field
  const updateField = useCallback(<K extends keyof ProducerFormData>(
    key: K,
    value: ProducerFormData[K]
  ) => {
    console.log('[useProducerEditorState] updateField', { key, value });
    hasLocalEditsRef.current = true;
    setFormData((prev) => ({ ...prev, [key]: value }));
    // Clear error when field is updated
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  }, [errors]);

  // Toggle category
  const toggleCategory = useCallback((category: string) => {
    setFormData((prev) => {
      const current = prev.categories;
      if (current.includes(category)) {
        return { ...prev, categories: current.filter((c) => c !== category) };
      }
      return { ...prev, categories: [...current, category] };
    });
  }, []);

  // Add custom category
  const addCategory = useCallback((category: string) => {
    const trimmed = category.trim();
    if (trimmed && !formData.categories.includes(trimmed)) {
      setFormData((prev) => ({ ...prev, categories: [...prev.categories, trimmed] }));
    }
  }, [formData.categories]);

  // Gallery URL management
  const updateGalleryUrl = useCallback((index: number, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.galleryUrls];
      updated[index] = value;
      return { ...prev, galleryUrls: updated };
    });
  }, []);

  const addGalleryUrl = useCallback(() => {
    setFormData((prev) => {
      if (prev.galleryUrls.length < 3) {
        return { ...prev, galleryUrls: [...prev.galleryUrls, ''] };
      }
      return prev;
    });
  }, []);

  const removeGalleryUrl = useCallback((index: number) => {
    setFormData((prev) => {
      const updated = prev.galleryUrls.filter((_, i) => i !== index);
      return { ...prev, galleryUrls: updated.length > 0 ? updated : [''] };
    });
  }, []);

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof ProducerFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Namn är obligatoriskt';
    }
    // short_description is now optional - only validate length if provided
    if (formData.shortDescription.length > 500) {
      newErrors.shortDescription = 'Max 500 tecken';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Save
  const save = useCallback(async () => {
    if (!validate()) return false;
    if (!onSave) return true;

    setIsSaving(true);
    try {
      await onSave(formData);
      setOriginalData(formData);
      hasLocalEditsRef.current = false;
      return true;
    } catch (error) {
      console.error('Failed to save:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [formData, onSave, validate]);

  // Reset to original
  const reset = useCallback(() => {
    setFormData(originalData);
    setErrors({});
    hasLocalEditsRef.current = false;
  }, [originalData]);

  return {
    formData,
    setFormData,
    updateField,
    toggleCategory,
    addCategory,
    updateGalleryUrl,
    addGalleryUrl,
    removeGalleryUrl,
    errors,
    setErrors,
    isDirty,
    isSaving,
    validate,
    save,
    reset,
  };
}
